import {
  WebsiteSettingsInterface,
  WebsiteSettingsState,
  WebsiteStylesInterface,
  WebsiteThemeInterface,
  WebsiteThemeListInterface,
  WebsiteFontSettingsInterface,
  WebsiteTypographySettingsStylesInterface,
} from './website-settings-types'
import {
  SET_WEBSITE_SETTINGS,
  SET_WEBSITE_STYLES,
  UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH,
  UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING,
  SET_WEBSITE_THEME,
  SET_WEBSITE_DRAFT_THEME,
  SET_WEBSITE_THEMES_LIST,
  UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY,
} from './website-settings-action-type'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { AnyAction } from 'redux'

const defaultReducerState: WebsiteSettingsState = {
  websiteStyles: undefined,
  websiteThemesList: undefined,
  websiteTheme: undefined,
  websiteDraftTheme: null,
  websiteId: undefined,
  websiteLayoutPageId: undefined,
  websiteLocales: [],
}

/* NOTE: mock
const defaultReducerState: WebsiteSettingsState = {
  websiteStyles: {
    typography: {
      paragraph: {
        fontFamily: 'Arial',
        fontSize: 16,
        fontWeight: '400',
        lineHeight: 1.5,
        textColor: '#000000',
        backgroundColor: '#ffffff',
      },
      heading: {
        fontFamily: 'Helvetica',
        fontSize: 24,
        fontWeight: '700',
        lineHeight: 1.2,
        textColor: '#000000',
        backgroundColor: '#ffffff',
      },
    },
    buttonsAndLinks: {
      primary: {
        typography: {
          fontFamily: 'Arial',
          fontSize: 16,
          fontWeight: '400',
          lineHeight: 1.5,
        },
      },
    },
  },
  websiteThemesList: undefined,
  websiteTheme: undefined,
  websiteId: 1,
  websiteLayoutPageId: 1,
  websiteLocales: [PageLocaleEnum.ENGLISH, PageLocaleEnum.FRENCH],
} */

export interface WebsiteStylesAction {
  type: typeof SET_WEBSITE_STYLES
  payload: WebsiteStylesInterface
}

export interface WebsiteStylesTypographyParagraphAction<
  T extends
    WebsiteTypographySettingsStylesInterface = WebsiteTypographySettingsStylesInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH
  payload: T
}

export interface WebsiteStylesTypographyHeadingAction<
  T extends
    WebsiteTypographySettingsStylesInterface = WebsiteTypographySettingsStylesInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING
  payload: T
}

export interface WebsiteStylesPrimaryButtonsTypographyAction<
  T extends WebsiteFontSettingsInterface = WebsiteFontSettingsInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY
  payload: T
}

export interface WebsiteThemeAction {
  type: typeof SET_WEBSITE_THEME
  payload: WebsiteThemeInterface
}

export interface WebsiteDraftThemeAction {
  type: typeof SET_WEBSITE_DRAFT_THEME
  payload: WebsiteThemeInterface
}

export interface WebsiteThemeListAction {
  type: typeof SET_WEBSITE_THEMES_LIST
  payload: WebsiteThemeListInterface[]
}

export interface WebsiteSettingsAction {
  type: typeof SET_WEBSITE_SETTINGS
  payload: WebsiteSettingsInterface
}

export type WebsiteSettingsActionType =
  | WebsiteStylesAction
  | WebsiteThemeAction
  | WebsiteDraftThemeAction
  | WebsiteThemeListAction
  | WebsiteSettingsAction
  | WebsiteStylesTypographyParagraphAction
  | WebsiteStylesTypographyHeadingAction
  | WebsiteStylesPrimaryButtonsTypographyAction

export const websiteSettingsReducer = (
  // biome-ignore lint/style/useDefaultParameterLast: <explanation>
  state: WebsiteSettingsState = defaultReducerState,
  action: WebsiteSettingsActionType | AnyAction,
): WebsiteSettingsState => {
  switch (action.type) {
    case SET_WEBSITE_STYLES:
      return {
        ...state,
        websiteStyles: action.payload,
      }
    case UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          typography: {
            ...state.websiteStyles?.typography,
            paragraph: action.payload,
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          typography: {
            ...state.websiteStyles?.typography,
            heading: action.payload,
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          buttonsAndLinks: {
            ...state.websiteStyles?.buttonsAndLinks,
            primary: {
              ...state.websiteStyles?.buttonsAndLinks?.primary,
              typography: action.payload,
            },
          },
        },
      }
    case SET_WEBSITE_THEMES_LIST:
      return {
        ...state,
        websiteThemesList: action.payload,
      }
    case SET_WEBSITE_THEME:
      return {
        ...state,
        websiteTheme: action.payload,
      }
    case SET_WEBSITE_DRAFT_THEME:
      return {
        ...state,
        websiteDraftTheme: action.payload,
      }
    case SET_WEBSITE_SETTINGS:
      return {
        ...state,
        websiteId: action.payload.id,
        websiteLayoutPageId: action.payload.layoutPageId,
        websiteLocales: action.payload.locales,
        websiteStyles: action.payload.style,
        websiteTheme: action.payload.theme,
      }
    default:
      return state
  }
}
